/**
 * Allys Project Detail Content
 * 
 * Beautiful marketing content sections for project detail pages
 * including overview, features, gallery, timeline, and CTA.
 */

'use client';

import { motion } from 'framer-motion';
import { Download, MapPin, ExternalLink } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MarkdownRenderer } from '@routeroyal/ui-core';
import { useCurrencyOptional } from '@/contexts/CurrencyContext';
import { CURRENCY_CONFIG, DEFAULT_CURRENCY, type Currency } from '@/lib/config/app.config';

const AllysMapComponent = dynamic(
  () => import('./AllysMapComponent').then(mod => mod.AllysMapComponent),
  { ssr: false }
);

interface UnitMixEntry {
  propertyTypeName: string;
  count: number;
}

// Project interface - matches PropertyDevelopmentProject
interface ProjectData {
  id: string;
  name: string;
  slug?: string | null;
  description?: string | null;
  projectType?: string | null;
  status?: string | null;
  stage?: string | null;
  totalUnits?: number | null;
  soldUnits?: number | null;
  availableUnits?: number | null;
  unitMixSummary?: string | null;
  unitMixDetails?: UnitMixEntry[] | Record<string, unknown> | null;
  displayPriceText?: string | null;
  heroImageUrl?: string | null;
  projectWebsiteUrl?: string | null;
  marketingStatusText?: string | null;
  estimatedStartDate?: string | null;
  estimatedCompletionDate?: string | null;
  developmentApprovalDate?: string | null;
  developmentApprovalStatus?: string | null;
}

// Media interface - matches MediaLink
interface MediaItem {
  id: string;
  mediaId?: string;
  usageContext?: string;
  sortOrder?: number;
  contextualTitle?: string | null;
  media: {
    id: string;
    url: string;
    thumbnailUrl?: string;
    filename?: string;
  };
}

/** Simplified listing card data for development unit display */
interface DevelopmentListingCard {
  id: string;
  listingId: string;
  property: {
    id: string;
    name: string;
    description: string | null;
    bedrooms: number | null;
    bathrooms: number | null;
    totalFloorArea: number | null;
    totalFloorAreaUnit: string | null;
    primaryImageUrl: string | null;
    currency: string;
  };
  location: {
    city: string | null;
    suburb: string | null;
  } | null;
  listingType: string;
  status: string;
  displayPriceText: string | null;
  mainHeadline: string | null;
  price: {
    amount: number;
    currency: string;
    frequency?: string | null;
  } | null;
  propertyType?: {
    id: string;
    name: string;
    icon: string | null;
  } | null;
}

/** Location data for map display */
interface LocationData {
  latitude?: number | null;
  longitude?: number | null;
  formattedAddress?: string | null;
  city?: string | null;
  suburb?: string | null;
  state?: string | null;
}

export interface AllysProjectDetailContentProps {
  project: ProjectData;
  media?: MediaItem[];
  brandColors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
  messages: {
    overview?: string;
    keyFeatures?: string;
    projectDetails?: string;
    gallery?: string;
    unitAvailability?: string;
    timeline?: string;
    location?: string;
    registerInterest?: string;
    downloadBrochure?: string;
    callUs?: string;
    emailUs?: string;
    projectType?: string;
    totalUnits?: string;
    availableUnits?: string;
    soldUnits?: string;
    estimatedCompletion?: string;
    developmentApproval?: string;
    projectStage?: string;
    unitMix?: string;
    unitTypes?: string;
    viewAllPhotos?: string;
    contactAgent?: string;
  };
  locale?: string;
  contactPhone?: string;
  contactEmail?: string;
  /** Listings (units) that belong to this development */
  developmentListings?: DevelopmentListingCard[];
  /** Base path for building property links (e.g. "/en") */
  basePath?: string;
  /** Development location for map display */
  location?: LocationData;
  /** URL to a downloadable brochure PDF */
  brochureUrl?: string;
}

function parseUnitMixDetails(
  raw: UnitMixEntry[] | Record<string, unknown> | null | undefined,
): UnitMixEntry[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.filter(
      (e): e is UnitMixEntry =>
        typeof e === 'object' && e !== null && 'propertyTypeName' in e && 'count' in e,
    );
  }
  return [];
}

/**
 * AllysProjectDetailContent - Marketing sections for project pages
 */
export function AllysProjectDetailContent({
  project,
  media = [],
  brandColors,
  messages,
  locale = 'en',
  contactPhone,
  contactEmail,
  developmentListings = [],
  basePath = '',
  location,
  brochureUrl,
}: AllysProjectDetailContentProps) {
  const [showAllGallery, setShowAllGallery] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [unitTypeFilter, setUnitTypeFilter] = useState<string | null>(null);
  const currencyContext = useCurrencyOptional();

  const unitPropertyTypes = useMemo(() => {
    const types = new Map<string, string>();
    for (const listing of developmentListings) {
      if (listing.propertyType?.name) {
        types.set(listing.propertyType.id, listing.propertyType.name);
      }
    }
    return Array.from(types.entries())
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([id, name]) => ({ id, name }));
  }, [developmentListings]);

  const filteredListings = useMemo(() => {
    if (!unitTypeFilter) return developmentListings;
    return developmentListings.filter(l => l.propertyType?.id === unitTypeFilter);
  }, [developmentListings, unitTypeFilter]);
  
  // Allys theme colors
  const colors = {
    primary: brandColors?.primary || '#243462',
    secondary: brandColors?.secondary || '#040813',
    accent: brandColors?.accent || '#BBA339',
  };
  
  // Filter gallery images
  const galleryImages = media.filter(
    m => m.usageContext === 'GALLERY' || m.usageContext === 'PRIMARY_HERO' || m.usageContext === 'AERIAL'
  );
  const displayedImages = showAllGallery ? galleryImages : galleryImages.slice(0, 6);
  
  // Calculate availability percentage
  const availabilityPercentage = project.totalUnits && project.soldUnits
    ? Math.round(((project.totalUnits - project.soldUnits) / project.totalUnits) * 100)
    : 100;
  
  // Format stage for display
  const formatStage = (stage: string | null): string => {
    if (!stage) return '';
    return stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };
  
  return (
    <div 
      className="bg-white font-inter"
    >
      {/* Overview Section */}
      <section data-track-section="overview" className="relative py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
            {/* Left Column - Overview */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              variants={fadeInUp}
            >
              {/* Section Label */}
              <div className="mb-6 flex items-center gap-3">
                <div 
                  className="h-1 w-12 rounded-full"
                  style={{ backgroundColor: colors.accent }}
                />
                <span 
                  className="text-sm font-semibold uppercase tracking-[0.2em]"
                  style={{ color: colors.accent }}
                >
                  {messages.overview || 'Overview'}
                </span>
              </div>
              
              <h2 
                className="mb-6 text-3xl font-bold leading-tight tracking-tight lg:text-4xl"
                style={{ color: colors.primary }}
              >
                About This Development
              </h2>
              
              <div>
                <MarkdownRenderer
                  content={project.description || ''}
                  theme="allys"
                />
              </div>
              
              {/* Marketing Status */}
              {project.marketingStatusText && (
                <div 
                  className="mt-8 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold"
                  style={{ 
                    backgroundColor: `${colors.accent}15`, 
                    color: colors.accent 
                  }}
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  {project.marketingStatusText}
                </div>
              )}

              {/* Brochure Download CTA */}
              {brochureUrl && (
                <div className="mt-8">
                  <a
                    href={brochureUrl}
                    download
                    className="group inline-flex items-center gap-3 rounded-xl px-6 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:scale-105 hover:shadow-xl"
                    style={{ backgroundColor: colors.primary }}
                  >
                    <Download className="h-5 w-5 transition-transform group-hover:-translate-y-0.5" />
                    {messages.downloadBrochure || 'Download Brochure'}
                    <ExternalLink className="h-4 w-4 opacity-50" />
                  </a>
                </div>
              )}
            </motion.div>
            
            {/* Right Column - Key Details Cards */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6, delay: 0.2 }}
              variants={fadeInUp}
              className="grid gap-4 sm:grid-cols-2"
            >
              {/* Project Type */}
              {project.projectType && (
                <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-6 transition-shadow hover:shadow-lg">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-gray-400">
                    {messages.projectType || 'Project Type'}
                  </div>
                  <div 
                    className="text-xl font-bold"
                    style={{ color: colors.primary }}
                  >
                    {project.projectType.replace(/_/g, ' ')}
                  </div>
                </div>
              )}
              
              {/* Total Units */}
              {project.totalUnits && (
                <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-6 transition-shadow hover:shadow-lg">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-gray-400">
                    {messages.totalUnits || 'Total Units'}
                  </div>
                  <div 
                    className="text-3xl font-bold"
                    style={{ color: colors.accent }}
                  >
                    {project.totalUnits}
                  </div>
                </div>
              )}
              
              {/* Available Units */}
              {project.availableUnits !== null && (
                <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-6 transition-shadow hover:shadow-lg">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-gray-400">
                    {messages.availableUnits || 'Available'}
                  </div>
                  <div className="text-3xl font-bold text-emerald-600">
                    {project.availableUnits}
                  </div>
                </div>
              )}
              
              {/* Estimated Completion */}
              {project.estimatedCompletionDate && (
                <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-6 transition-shadow hover:shadow-lg">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-gray-400">
                    {messages.estimatedCompletion || 'Completion'}
                  </div>
                  <div 
                    className="text-xl font-bold"
                    style={{ color: colors.primary }}
                  >
                    {new Date(project.estimatedCompletionDate).toLocaleDateString(locale, { 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </div>
                </div>
              )}
              
              {/* Project Stage */}
              {project.stage && (
                <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-6 transition-shadow hover:shadow-lg sm:col-span-2">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-gray-400">
                    {messages.projectStage || 'Current Stage'}
                  </div>
                  <div 
                    className="text-xl font-bold"
                    style={{ color: colors.primary }}
                  >
                    {formatStage(project.stage)}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Unit Availability Section */}
      {project.totalUnits && (
        <section 
          data-track-section="units"
          className="relative py-20 lg:py-28"
          style={{ backgroundColor: colors.primary }}
        >
          {/* Decorative pattern */}
          <div 
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
          
          <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              variants={fadeInUp}
              className="text-center"
            >
              {/* Section Label */}
              <div className="mb-6 flex items-center justify-center gap-3">
                <div 
                  className="h-1 w-12 rounded-full"
                  style={{ backgroundColor: colors.accent }}
                />
                <span 
                  className="text-sm font-semibold uppercase tracking-[0.2em]"
                  style={{ color: colors.accent }}
                >
                  {messages.unitAvailability || 'Availability'}
                </span>
                <div 
                  className="h-1 w-12 rounded-full"
                  style={{ backgroundColor: colors.accent }}
                />
              </div>
              
              <h2 className="mb-12 text-3xl font-bold text-white lg:text-4xl">
                Unit Availability
              </h2>
              
              {/* Availability Stats */}
              <div className="mx-auto max-w-3xl">
                {/* Progress Bar */}
                <div className="mb-8">
                  <div className="mb-4 flex justify-between text-sm font-medium text-white/80">
                    <span>{project.availableUnits || 0} Available</span>
                    <span>{project.soldUnits || 0} Sold</span>
                  </div>
                  <div className="h-4 w-full overflow-hidden rounded-full bg-white/20">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${availabilityPercentage}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: colors.accent }}
                    />
                  </div>
                  <div 
                    className="mt-3 text-center text-2xl font-bold"
                    style={{ color: colors.accent }}
                  >
                    {availabilityPercentage}% Available
                  </div>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center">
                    <div 
                      className="mb-2 text-4xl font-bold lg:text-5xl"
                      style={{ color: colors.accent }}
                    >
                      {project.totalUnits}
                    </div>
                    <div className="text-sm font-medium uppercase tracking-wider text-white/60">
                      {messages.totalUnits || 'Total Units'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="mb-2 text-4xl font-bold text-emerald-400 lg:text-5xl">
                      {project.availableUnits || 0}
                    </div>
                    <div className="text-sm font-medium uppercase tracking-wider text-white/60">
                      {messages.availableUnits || 'Available'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="mb-2 text-4xl font-bold text-white/50 lg:text-5xl">
                      {project.soldUnits || 0}
                    </div>
                    <div className="text-sm font-medium uppercase tracking-wider text-white/60">
                      {messages.soldUnits || 'Sold'}
                    </div>
                  </div>
                </div>
                
                {/* Unit Mix Breakdown */}
                {(() => {
                  const entries = parseUnitMixDetails(project.unitMixDetails);
                  if (entries.length > 0) {
                    return (
                      <div className="mt-10 rounded-2xl bg-white/10 p-6 backdrop-blur-sm">
                        <div className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-white/60">
                          {messages.unitTypes || 'Unit Types'}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {entries.map((entry) => (
                            <span
                              key={entry.propertyTypeName}
                              className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm text-white/90 backdrop-blur-sm"
                            >
                              {entry.propertyTypeName}
                              <span className="text-white/50">({entry.count})</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  if (project.unitMixSummary) {
                    return (
                      <div className="mt-10 rounded-2xl bg-white/10 p-6 backdrop-blur-sm">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-white/60">
                          {messages.unitMix || 'Unit Mix'}
                        </div>
                        <p className="text-lg text-white/90">
                          {project.unitMixSummary}
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </motion.div>
          </div>
        </section>
      )}
      
      {/* Development Units / Properties Section */}
      {developmentListings.length > 0 && (
        <section data-track-section="units" className="py-20 lg:py-28 bg-gray-50">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
              }}
            >
              <div className="mb-8">
                <span
                  className="mb-3 inline-block text-sm font-bold uppercase tracking-widest"
                  style={{ color: colors.accent }}
                >
                  Properties
                </span>
                <h2
                  className="text-3xl font-bold lg:text-4xl"
                  style={{ color: colors.primary }}
                >
                  Available Units
                </h2>
              </div>

              {unitPropertyTypes.length > 1 && (
                <div className="mb-10 flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => setUnitTypeFilter(null)}
                    className="rounded-full border px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors"
                    style={
                      !unitTypeFilter
                        ? { backgroundColor: colors.primary, borderColor: colors.primary, color: '#fff' }
                        : { borderColor: '#d6d3d1', color: '#78716c' }
                    }
                  >
                    All ({developmentListings.length})
                  </button>
                  {unitPropertyTypes.map(pt => {
                    const count = developmentListings.filter(l => l.propertyType?.id === pt.id).length;
                    return (
                      <button
                        key={pt.id}
                        onClick={() => setUnitTypeFilter(unitTypeFilter === pt.id ? null : pt.id)}
                        className="rounded-full border px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors"
                        style={
                          unitTypeFilter === pt.id
                            ? { backgroundColor: colors.primary, borderColor: colors.primary, color: '#fff' }
                            : { borderColor: '#d6d3d1', color: '#78716c' }
                        }
                      >
                        {pt.name} ({count})
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filteredListings.map((listing) => (
                  <Link
                    key={listing.id}
                    href={`${basePath}/properties/${listing.property.id}`}
                    className="group block overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                      {listing.property.primaryImageUrl ? (
                        <Image
                          src={listing.property.primaryImageUrl}
                          alt={listing.property.name}
                          fill
                          className="object-cover transition-transform duration-500 group-hover:scale-110"
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-400">
                          No Image
                        </div>
                      )}
                      <div
                        className={`absolute left-3 top-3 rounded-full px-3 py-1.5 text-xs font-bold text-white shadow-lg ${
                          listing.status === 'SOLD' ? 'bg-gray-700' : ''
                        }`}
                        style={listing.status !== 'SOLD' ? { backgroundColor: listing.listingType === 'SALE' ? colors.primary : colors.accent } : undefined}
                      >
                        {listing.status === 'SOLD' ? 'Sold' : listing.listingType === 'SALE' ? 'For Sale' : listing.listingType === 'RENTAL' ? 'For Rent' : listing.listingType}
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="mb-2 flex items-center gap-2">
                        <h3
                          className="text-lg font-bold transition-colors group-hover:text-[#BBA339]"
                          style={{ color: colors.primary }}
                        >
                          {listing.mainHeadline || listing.property.name}
                        </h3>
                        {listing.propertyType?.name && (
                          <span
                            className="shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                            style={{ borderColor: `${colors.accent}40`, color: colors.accent }}
                          >
                            {listing.propertyType.name}
                          </span>
                        )}
                      </div>
                      <div className="mb-3 text-xl font-extrabold" style={{ color: colors.primary }}>
                        {(() => {
                          const priceAmount = listing.price?.amount;
                          const priceCurrency = listing.price?.currency;
                          if (!priceAmount || !priceCurrency) {
                            return listing.displayPriceText || 'Price on request';
                          }
                          const selectedCurrency = currencyContext?.selectedCurrency ?? DEFAULT_CURRENCY;
                          let displayAmount = priceAmount;
                          let displayCurrency = priceCurrency.toUpperCase();

                          if (displayCurrency !== selectedCurrency && currencyContext?.rates) {
                            const converted = currencyContext.convertBetween(priceAmount, displayCurrency, selectedCurrency);
                            if (converted !== null) {
                              displayAmount = converted;
                              displayCurrency = selectedCurrency;
                            }
                          }

                          const config = CURRENCY_CONFIG[displayCurrency as Currency];
                          if (!config) {
                            return listing.displayPriceText || new Intl.NumberFormat(locale, {
                              style: 'currency',
                              currency: priceCurrency,
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(priceAmount);
                          }
                          return new Intl.NumberFormat(config.locale, {
                            style: 'currency',
                            currency: displayCurrency,
                            minimumFractionDigits: config.decimalPlaces,
                            maximumFractionDigits: config.decimalPlaces,
                          }).format(displayAmount);
                        })()}
                      </div>
                      <div className="flex flex-wrap gap-4 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                        {listing.property.bedrooms != null && listing.property.bedrooms > 0 && (
                          <span className="flex items-center gap-1.5">
                            <span className="font-semibold">{listing.property.bedrooms}</span> bed
                          </span>
                        )}
                        {listing.property.bathrooms != null && listing.property.bathrooms > 0 && (
                          <span className="flex items-center gap-1.5">
                            <span className="font-semibold">{listing.property.bathrooms}</span> bath
                          </span>
                        )}
                        {listing.property.totalFloorArea != null && listing.property.totalFloorArea > 0 && (
                          <span className="flex items-center gap-1.5">
                            <span className="font-semibold">{listing.property.totalFloorArea.toLocaleString()}</span>
                            {listing.property.totalFloorAreaUnit?.toLowerCase() || 'sqm'}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section data-track-section="gallery" className="py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              variants={fadeInUp}
            >
              {/* Section Header */}
              <div className="mb-12 flex items-end justify-between">
                <div>
                  <div className="mb-4 flex items-center gap-3">
                    <div 
                      className="h-1 w-12 rounded-full"
                      style={{ backgroundColor: colors.accent }}
                    />
                    <span 
                      className="text-sm font-semibold uppercase tracking-[0.2em]"
                      style={{ color: colors.accent }}
                    >
                      {messages.gallery || 'Gallery'}
                    </span>
                  </div>
                  <h2 
                    className="text-3xl font-bold lg:text-4xl"
                    style={{ color: colors.primary }}
                  >
                    Project Gallery
                  </h2>
                </div>
                
                {galleryImages.length > 6 && (
                  <button
                    onClick={() => setShowAllGallery(!showAllGallery)}
                    className="hidden items-center gap-2 text-sm font-semibold transition-colors hover:opacity-80 sm:inline-flex"
                    style={{ color: colors.accent }}
                  >
                    {showAllGallery ? 'Show Less' : messages.viewAllPhotos || `View All ${galleryImages.length} Photos`}
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Gallery Grid */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {displayedImages.map((item, index) => (
                  <motion.button
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    onClick={() => setLightboxIndex(galleryImages.indexOf(item))}
                    className={`group relative overflow-hidden rounded-2xl text-left ${
                      index === 0 ? 'sm:col-span-2 sm:row-span-2' : ''
                    }`}
                    style={{ aspectRatio: index === 0 ? '16/10' : '4/3' }}
                  >
                    <Image
                      src={item.media.url}
                      alt={item.contextualTitle || project.name}
                      fill
                      className="object-cover transition-transform duration-700 group-hover:scale-110"
                      sizes={index === 0 ? '(max-width: 768px) 100vw, 66vw' : '(max-width: 768px) 100vw, 33vw'}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    {item.contextualTitle && (
                      <div className="absolute bottom-4 left-4 right-4 translate-y-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                        <span className="text-sm font-medium text-white">
                          {item.contextualTitle}
                        </span>
                      </div>
                    )}
                  </motion.button>
                ))}
              </div>

              {/* Gallery Lightbox */}
              {lightboxIndex !== null && (
                <GalleryLightbox
                  images={galleryImages.map(item => ({
                    url: item.media.url,
                    thumbnailUrl: item.media.thumbnailUrl || item.media.url,
                    alt: item.contextualTitle || project.name,
                  }))}
                  initialIndex={lightboxIndex}
                  projectName={project.name}
                  onClose={() => setLightboxIndex(null)}
                />
              )}
              
              {/* Mobile View All Button */}
              {galleryImages.length > 6 && (
                <button
                  onClick={() => setShowAllGallery(!showAllGallery)}
                  className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl border-2 py-3 text-sm font-semibold transition-colors sm:hidden"
                  style={{ borderColor: colors.accent, color: colors.accent }}
                >
                  {showAllGallery ? 'Show Less' : messages.viewAllPhotos || `View All ${galleryImages.length} Photos`}
                </button>
              )}
            </motion.div>
          </div>
        </section>
      )}
      
      {/* Timeline Section */}
      {(project.estimatedStartDate || project.estimatedCompletionDate || project.developmentApprovalDate) && (
        <section data-track-section="timeline" className="bg-gray-50 py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              variants={fadeInUp}
            >
              {/* Section Header */}
              <div className="mb-12 text-center">
                <div className="mb-4 flex items-center justify-center gap-3">
                  <div 
                    className="h-1 w-12 rounded-full"
                    style={{ backgroundColor: colors.accent }}
                  />
                  <span 
                    className="text-sm font-semibold uppercase tracking-[0.2em]"
                    style={{ color: colors.accent }}
                  >
                    {messages.timeline || 'Timeline'}
                  </span>
                  <div 
                    className="h-1 w-12 rounded-full"
                    style={{ backgroundColor: colors.accent }}
                  />
                </div>
                <h2 
                  className="text-3xl font-bold lg:text-4xl"
                  style={{ color: colors.primary }}
                >
                  Project Timeline
                </h2>
              </div>
              
              {/* Timeline */}
              <div className="mx-auto max-w-3xl">
                <div className="relative">
                  {/* Timeline Line */}
                  <div 
                    className="absolute left-6 top-0 bottom-0 w-0.5"
                    style={{ backgroundColor: `${colors.accent}30` }}
                  />
                  
                  {/* Timeline Items */}
                  <div className="space-y-8">
                    {project.developmentApprovalDate && (
                      <div className="relative flex gap-6">
                        <div 
                          className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-4 border-white shadow-lg"
                          style={{ backgroundColor: colors.accent }}
                        >
                          <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="flex-1 rounded-2xl bg-white p-6 shadow-lg">
                          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-400">
                            {messages.developmentApproval || 'Development Approval'}
                          </div>
                          <div className="mt-1 text-xl font-bold" style={{ color: colors.primary }}>
                            {new Date(project.developmentApprovalDate).toLocaleDateString(locale, { 
                              month: 'long', 
                              year: 'numeric' 
                            })}
                          </div>
                          {project.developmentApprovalStatus && (
                            <span 
                              className="mt-2 inline-block rounded-full px-3 py-1 text-xs font-semibold"
                              style={{ backgroundColor: `${colors.accent}15`, color: colors.accent }}
                            >
                              {project.developmentApprovalStatus}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {project.estimatedStartDate && (
                      <div className="relative flex gap-6">
                        <div 
                          className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-4 border-white shadow-lg"
                          style={{ backgroundColor: colors.primary }}
                        >
                          <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1 rounded-2xl bg-white p-6 shadow-lg">
                          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-400">
                            Construction Start
                          </div>
                          <div className="mt-1 text-xl font-bold" style={{ color: colors.primary }}>
                            {new Date(project.estimatedStartDate).toLocaleDateString(locale, { 
                              month: 'long', 
                              year: 'numeric' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {project.estimatedCompletionDate && (
                      <div className="relative flex gap-6">
                        <div 
                          className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-4 border-white shadow-lg"
                          style={{ backgroundColor: '#10b981' }}
                        >
                          <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                        </div>
                        <div className="flex-1 rounded-2xl bg-white p-6 shadow-lg">
                          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-400">
                            {messages.estimatedCompletion || 'Estimated Completion'}
                          </div>
                          <div className="mt-1 text-xl font-bold" style={{ color: colors.primary }}>
                            {new Date(project.estimatedCompletionDate).toLocaleDateString(locale, { 
                              month: 'long', 
                              year: 'numeric' 
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}
      
      {/* Location Map Section */}
      {location?.latitude && location?.longitude && (
        <section data-track-section="location" className="py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              variants={fadeInUp}
            >
              <div className="mb-12">
                <div className="mb-4 flex items-center gap-3">
                  <div 
                    className="h-1 w-12 rounded-full"
                    style={{ backgroundColor: colors.accent }}
                  />
                  <span 
                    className="text-sm font-semibold uppercase tracking-[0.2em]"
                    style={{ color: colors.accent }}
                  >
                    {messages.location || 'Location'}
                  </span>
                </div>
                <h2 
                  className="text-3xl font-bold lg:text-4xl"
                  style={{ color: colors.primary }}
                >
                  Project Location
                </h2>
                {location.formattedAddress && (
                  <p className="mt-3 flex items-center gap-2 text-gray-600">
                    <MapPin className="h-4 w-4" style={{ color: colors.accent }} />
                    {location.formattedAddress}
                  </p>
                )}
              </div>

              <div className="overflow-hidden rounded-2xl">
                <AllysMapComponent
                  latitude={location.latitude}
                  longitude={location.longitude}
                  title={project.name}
                  {...(location.formattedAddress ? { description: location.formattedAddress } : {})}
                  height="450px"
                  zoom={14}
                  enableDirections
                  onGetDirections={() => {
                    window.open(
                      `https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`,
                      '_blank'
                    );
                  }}
                />
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section 
        data-track-section="contact"
        className="relative py-20 lg:py-28"
        style={{ backgroundColor: colors.secondary }}
      >
        {/* Decorative gradient */}
        <div 
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at top right, ${colors.accent}20 0%, transparent 50%), radial-gradient(ellipse at bottom left, ${colors.primary}30 0%, transparent 50%)`,
          }}
        />
        
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
            variants={fadeInUp}
            className="text-center"
          >
            <h2 className="mb-6 text-3xl font-bold text-white lg:text-5xl">
              Interested in {project.name}?
            </h2>
            <p className="mx-auto mb-10 max-w-2xl text-lg text-white/70">
              Register your interest today and be the first to receive updates on availability, pricing, and exclusive offers.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href={`/${locale}/contact?project=${project.slug || project.id}`}
                className="group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-bold text-white transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                style={{ backgroundColor: colors.accent }}
              >
                {messages.registerInterest || 'Register Interest'}
                <svg 
                  className="h-5 w-5 transition-transform group-hover:translate-x-1" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>

              {brochureUrl && (
                <a
                  href={brochureUrl}
                  download
                  className="inline-flex items-center justify-center gap-2 rounded-full border-2 px-8 py-4 text-base font-bold text-white transition-all duration-300 hover:bg-white/10"
                  style={{ borderColor: colors.accent, color: colors.accent }}
                >
                  <Download className="h-5 w-5" />
                  {messages.downloadBrochure || 'Download Brochure'}
                </a>
              )}
              
              {contactPhone && (
                <a
                  href={`tel:${contactPhone}`}
                  className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/30 px-8 py-4 text-base font-bold text-white transition-all duration-300 hover:border-white hover:bg-white/10"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {messages.callUs || 'Call Us'}
                </a>
              )}
            </div>
            
            {/* Contact Info */}
            <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-white/60">
              {contactPhone && (
                <a href={`tel:${contactPhone}`} className="flex items-center gap-2 hover:text-white/90">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {contactPhone}
                </a>
              )}
              {contactEmail && (
                <a href={`mailto:${contactEmail}`} className="flex items-center gap-2 hover:text-white/90">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {contactEmail}
                </a>
              )}
              {project.projectWebsiteUrl && (
                <a 
                  href={project.projectWebsiteUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-white/90"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  Project Website
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

// =============================================================================
// Gallery Lightbox - Fullscreen image viewer
// =============================================================================

interface LightboxImage {
  url: string;
  thumbnailUrl?: string;
  alt?: string;
}

function GalleryLightbox({
  images,
  initialIndex,
  projectName,
  onClose,
}: {
  images: LightboxImage[];
  initialIndex: number;
  projectName: string;
  onClose: () => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  useEffect(() => {
    setIsZoomed(false);
  }, [currentIndex]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, goToPrevious, goToNext]);

  const onTouchStartHandler = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0]?.clientX || null);
  };
  const onTouchMoveHandler = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0]?.clientX || null);
  };
  const onTouchEndHandler = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) goToNext();
    if (distance < -50) goToPrevious();
  };

  const handleDownload = () => {
    const current = images[currentIndex];
    if (!current) return;
    const link = document.createElement('a');
    link.href = current.url;
    link.download = `${projectName}-${currentIndex + 1}.jpg`;
    link.click();
  };

  const current = images[currentIndex];
  if (!current) return null;

  return (
    <div className="fixed inset-0 z-9999 bg-black/95 backdrop-blur-sm">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-linear-to-b from-black/80 to-transparent p-4 md:p-6">
        <div className="flex items-center justify-between mx-auto max-w-screen-2xl">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label="Close gallery"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="hidden md:block">
              <p className="text-white font-semibold text-lg">{projectName}</p>
              <p className="text-white/70 text-sm">{currentIndex + 1} of {images.length}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <p className="md:hidden text-white/90 text-sm font-medium">{currentIndex + 1} / {images.length}</p>
            <button
              onClick={() => setIsZoomed(!isZoomed)}
              className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label={isZoomed ? 'Zoom out' : 'Zoom in'}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isZoomed ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                )}
              </svg>
            </button>
            <button
              onClick={handleDownload}
              className="hidden md:block p-2 text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label="Download image"
            >
              <Download className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Image */}
      <div
        className="absolute inset-0 flex items-center justify-center p-4 md:p-20"
        onTouchStart={onTouchStartHandler}
        onTouchMove={onTouchMoveHandler}
        onTouchEnd={onTouchEndHandler}
      >
        <div className={`relative w-full h-full transition-transform duration-300 ${isZoomed ? 'scale-150' : 'scale-100'}`}>
          <Image
            src={current.url}
            alt={current.alt || `${projectName} - Image ${currentIndex + 1}`}
            fill
            className="object-contain"
            sizes="100vw"
            priority
            quality={95}
          />
        </div>
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 md:p-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full transition-all hover:scale-110 z-10"
            aria-label="Previous image"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 md:p-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full transition-all hover:scale-110 z-10"
            aria-label="Next image"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Thumbnails */}
      <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/80 to-transparent p-4 md:p-6">
        <div className="mx-auto max-w-screen-2xl overflow-x-auto">
          <div className="flex gap-2 md:gap-3 justify-center min-w-max px-4">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`relative shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden transition-all ${
                  index === currentIndex
                    ? 'ring-2 ring-white scale-110'
                    : 'opacity-50 hover:opacity-100'
                }`}
              >
                <Image
                  src={image.thumbnailUrl || image.url}
                  alt={`Thumbnail ${index + 1}`}
                  fill
                  className="object-cover"
                  sizes="80px"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
